import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchTipStep,
  fetchTipSteps,
  updateTipStep,
  createTipStep,
  destroyTipStep,
  reorderTipSteps
} from "../../actions/mine_craft/tipStepsActions";

export interface TipStepInterface {
  id: number;
  tip_id: number;
  title: string;
  title_translations: { [key: string]: string | undefined };
  empty_locales: string[];
  image_url: string;
  order: number;
  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: TipStepInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,
};

const adaptor = createEntityAdapter<TipStepInterface>();

const slice = createSlice({
  name: 'tipSteps',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchTipSteps.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchTipSteps.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.tip_steps;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchTipSteps.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchTipStep.fulfilled, (state, action) => {
        const tipStep = Object.values(action.payload.tip_step)[0];
        state.entities[tipStep.id] = tipStep;
      })

      // #create
      .addCase(createTipStep.fulfilled, (state, action) => {
        const tipStep = Object.values(action.payload.tip_step)[0];
        state.entities[tipStep.id] = tipStep;
        state.ids.push(tipStep.id);
      })
      .addCase(createTipStep.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateTipStep.fulfilled, (state, action) => {
        const tipStep = Object.values(action.payload.tip_step)[0];
        state.entities[tipStep.id] = tipStep;
      })
      .addCase(updateTipStep.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #destroy
      .addCase(destroyTipStep.fulfilled, (state, action) => {
        adaptor.removeOne(state, action.payload.id);
      })

      // #reorder
      .addCase(reorderTipSteps.fulfilled, (state, action) => {
        state.ids = action.payload.ids;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectTipStepById,
  selectIds: selectTipStepIds,
  selectEntities: selectTipStepEntities,
  selectAll: selectAllTipSteps,
  selectTotal: selectTotalTipSteps
} = adaptor.getSelectors<RootState>(state => state.mineCraft.tipSteps);
